/**
 * The dashboard.
 *
 * Everything on one screen: what exists, whether it worked, when it last
 * worked, and how many players it produced. Failing sources come first, because
 * the things needing attention should be the things seen first — the server
 * sorts them, and this screen preserves that order.
 *
 * The tiles above the table summarise **the workspace**, not the table: they
 * are counted from the same list the table draws from, so neither can report a
 * source the other does not have, but they deliberately ignore the search and
 * the filters below them. A filter is a question about the table; "how many
 * sources are there" is not a question a filter should be able to change the
 * answer to. What the filters do change, the table's own header says.
 *
 * The table can be searched, filtered by sport and league, and ordered by any
 * of its columns. None of that arithmetic is here — it is all in
 * `../source-list`, free of React so `tests/source-list.test.ts` can assert it
 * under bare node; this screen holds the controls and the state they write to.
 *
 * Rows can be ticked and deleted together. Retiring a season's worth of
 * sources was eighteen visits to eighteen detail screens, which is the sort of
 * chore that ends with the wrong one deleted; the arithmetic behind the ticks
 * lives in `../selection`, and this screen only wires it to the table.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type SourceProfile } from "../api";
import { POLL_LIST_MS, keys } from "../queries";
import { Layout } from "../components/Layout";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Muted,
  Select,
  Spinner,
  StatTile,
  StatusBadge,
  TableScroll,
  TextInput,
} from "../components/ui";
import { PlayIcon, PlusIcon, SortIcon, SourcesIcon, TrashIcon } from "../components/icons";
import { formatRelative } from "../format";
import {
  retain,
  stateOf,
  summariseDeletion,
  toggle,
  toggleShown,
  type DeletionOutcome,
} from "../selection";
import {
  EVERYTHING,
  addressFor,
  arrange,
  chooseSport,
  isNarrowed,
  leaguesFor,
  narrowingFromAddress,
  nextSort,
  offering,
  sportsOf,
  type Narrowing,
  type Sort,
  type SortColumn,
} from "../source-list";

export function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * What the table is being asked for, and in what order.
   *
   * **The narrowing lives in the page address; the order does not.** The
   * address was once deliberately left alone, on the grounds that this list is
   * not a thing people send links to. The Dashboard made it one: its sport rows
   * open this list filtered to their sport, and a link can only say that
   * through the address. So the search, the sport and the league are read from
   * and written to `?q=&sport=&league=` — replacing the history entry rather
   * than adding one per keystroke.
   *
   * The order stays this screen's own, for the reason that kept everything here
   * before: a `?sort=` would outlive the sources it named, and nothing links to
   * an ordering.
   */
  const [address, setAddress] = useSearchParams();
  const narrowing = useMemo(() => narrowingFromAddress(address), [address]);
  const setNarrowing = (update: Narrowing | ((before: Narrowing) => Narrowing)) =>
    setAddress(
      (current) =>
        addressFor(typeof update === "function" ? update(narrowingFromAddress(current)) : update),
      { replace: true },
    );
  const [sort, setSort] = useState<Sort | null>(null);
  /**
   * Sources this screen has deleted, kept until the server stops sending them.
   *
   * **A deleted row must not be able to come back, and dropping it from the
   * cache alone cannot promise that.** This list polls every four seconds and
   * the API answers it in a second or more, so a list request sent before the
   * delete can land after it — carrying, truthfully as of when it was asked,
   * the row that has just gone. React Query cancels the request in flight when
   * the invalidation below fires, which closes the window most of the time;
   * "most of the time" is the wrong guarantee for the one action on this screen
   * that cannot be undone, and an operator who deletes a source and watches it
   * sit there has no way to tell a slow refresh from a delete that failed. The
   * reported symptom was exactly that: deleted, still on screen, gone after a
   * browser refresh.
   *
   * So the rows are also buried here, and the table renders what the server
   * says minus what this screen knows it deleted. Timing cannot reach it.
   */
  const [deleted, setDeleted] = useState<string[]>([]);

  const {
    data: profiles,
    error: loadError,
  } = useQuery({
    queryKey: keys.profiles,
    queryFn: api.listProfiles,
    // A Run in flight changes this screen without anyone touching it.
    refetchInterval: POLL_LIST_MS,
  });

  const startRun = useMutation({
    mutationFn: (profile: SourceProfile) => api.startRun(profile.id, false),
    // Straight to the Run: the list this screen shows is about to be stale
    // anyway, and the Run screen refetches it on the way back.
    onSuccess: (run) => navigate(`/runs/${run.id}`),
  });

  /**
   * What the server sent, minus what this screen has deleted.
   *
   * `retain` does the pruning, exactly as it does for the selection below: the
   * tombstones kept are the ones the server is *still* sending, so a refetch
   * that has caught up drops them by itself and nothing has to remember to
   * clear them. Everything downstream — the table, the tiles, the selection —
   * reads `visible`, never the raw list, or the two would disagree about how
   * many sources exist while a delete settles.
   */
  const servedIds = useMemo(() => (profiles ?? []).map((profile) => profile.id), [profiles]);
  const buried = retain(deleted, servedIds);
  const visible = useMemo(
    () => (profiles ?? []).filter((profile) => !buried.includes(profile.id)),
    [profiles, buried],
  );
  const ids = useMemo(() => visible.map((profile) => profile.id), [visible]);

  /**
   * The rows the table actually draws: the workspace, narrowed and ordered.
   *
   * Everything about the table reads this; everything about the *workspace* —
   * the tiles, the selection, the empty state that offers to add a first source
   * — reads `visible`. Keeping the two apart is what lets a filter hide rows
   * without the screen claiming they do not exist.
   */
  const shown = useMemo(() => arrange(visible, narrowing, sort), [visible, narrowing, sort]);
  const shownIds = useMemo(() => shown.map((profile) => profile.id), [shown]);
  const narrowed = isNarrowed(narrowing);

  /**
   * The selection, minus anything the workspace no longer holds.
   *
   * Read through `retain` rather than pruned into state by an effect: the list
   * polls, so a source deleted in another tab has to stop counting without a
   * render-time write to make it so. Every read below uses this, never
   * `selected`.
   *
   * Pruned against `ids` — every source — rather than against the rows on
   * screen. **Narrowing the table must not untick anything**: somebody ticking
   * three sources and then typing a term to find the fourth has not changed
   * their mind about the three, and a filter that silently emptied the
   * selection would make the one irreversible action on this screen depend on
   * what was typed in a search box. What the filter may not do silently it says
   * instead: `hidden` below is how many of the ticks the table is not drawing,
   * and the bar and the confirmation both report it.
   */
  const chosen = retain(selected, ids);
  const chosenHere = retain(chosen, shownIds);
  const hidden = chosen.length - chosenHere.length;

  /**
   * Change the selection, and retire the last delete's report with it.
   *
   * That report names sources by a selection that no longer exists, so leaving
   * it up while somebody ticks the next batch attaches an old failure to new
   * rows. Every path that edits the selection goes through here except the one
   * inside `bulkDelete`, which writes the report it has just produced.
   */
  const choose = (next: readonly string[]) => {
    setSelected([...next]);
    setNotice(null);
    /**
     * And retire a confirmation that is open, for the same reason.
     *
     * "Delete 4 sources? This cannot be undone." names a selection, and the
     * selection has just changed underneath it. The bar it sits in is hidden
     * while nothing is ticked, so a confirmation left standing does not go
     * away — it waits, invisible, and reappears over whatever is ticked next.
     * Asking again costs one click; a yes meant for four rows landing on a
     * different two does not.
     */
    setConfirming(false);
  };

  /**
   * Clicking a column heading: order by it, flip it, let it go.
   *
   * The third click is the one worth having. The server sorts failing sources
   * first and this screen has always preserved that; a header that only
   * toggled between ascending and descending would make it unreachable without
   * a reload.
   */
  const reorder = (column: SortColumn) => setSort((before) => nextSort(before, column));
  const chosenProfiles = useMemo(
    () => visible.filter((profile) => chosen.includes(profile.id)),
    [visible, chosen],
  );
  // Over the rows on screen, which is what the box at the top of them speaks
  // for. `stateOf` compares lengths, so handing it a selection reaching past
  // the table would read as "all" above a table only part of which is ticked.
  const headerState = stateOf(chosenHere, shownIds);

  /** The two facet menus, built from the workspace the table is drawn from. */
  const sports = useMemo(() => offering(sportsOf(visible), narrowing.sport), [visible, narrowing.sport]);
  const leagues = useMemo(
    () => offering(leaguesFor(visible, narrowing.sport), narrowing.league),
    [visible, narrowing.sport, narrowing.league],
  );

  /**
   * Deleting several sources is several requests.
   *
   * The API deletes one profile at a time and a bulk route would be a second
   * way to do what `DELETE /source-profiles/{id}` already does — with its own
   * authorisation and its own cascade to keep in step. So the screen sends the
   * requests it already has, a few at a time, and `allSettled` collects every
   * outcome: one refusal cannot hide the deletions that worked, and the
   * operator is told which is which.
   */
  const bulkDelete = useMutation({
    mutationFn: async (targets: SourceProfile[]) => {
      const outcomes: DeletionOutcome[] = [];
      /**
       * A few at a time, not all at once.
       *
       * The case this screen was built for is retiring a season, which is
       * eighteen sources in this workspace and more in the next. Deleting a
       * Source Profile cascades to its Bindings, its Credential Set and every
       * Run it recorded, so eighteen simultaneous cascades is a lot of
       * contention for the same tables over a connection pool that has other
       * callers. HTTP/1.1 would have queued them at six anyway; this makes
       * that the intent rather than an accident of the transport.
       */
      for (let start = 0; start < targets.length; start += DELETE_CONCURRENCY) {
        const batch = targets.slice(start, start + DELETE_CONCURRENCY);
        const results = await Promise.allSettled(
          batch.map((target) => api.deleteProfile(target.id)),
        );
        batch.forEach((target, index) => {
          outcomes.push({
            id: target.id,
            name: target.name,
            failure: failureOf(results[index]),
          });
        });
      }
      return summariseDeletion(outcomes);
    },
    onSuccess: async (report) => {
      const gone = new Set(report.deleted);
      // Buried first, and before anything that can await: from here the table
      // cannot show these rows again whatever any request in flight answers.
      setDeleted((before) => [...before, ...report.deleted]);
      /**
       * Then the request already in flight, if there is one.
       *
       * It was sent before these deletes landed, so its answer still names the
       * rows that have just gone — and letting it resolve would write them
       * straight back over the cache edited below. Cancelling keeps the data
       * already there, which is what the next line is about to correct.
       */
      await queryClient.cancelQueries({ queryKey: keys.profiles });
      /**
       * The list is in the cache and still fresh, so the deleted rows would
       * stay on screen until `staleTime` expired. Dropping them makes the
       * table right at once; the invalidation is what makes the server agree.
       * Their own entries go, because nothing can refetch a profile the server
       * no longer has.
       */
      queryClient.setQueryData<SourceProfile[]>(keys.profiles, (list) =>
        list?.filter((entry) => !gone.has(entry.id)),
      );
      for (const id of report.deleted) {
        queryClient.removeQueries({ queryKey: keys.profile(id) });
      }
      queryClient.invalidateQueries({ queryKey: keys.profiles });

      // Whatever refused stays ticked, so retrying is one click rather than a
      // hunt through the table for the rows that did not go.
      setSelected(report.failed);
      setConfirming(false);
      setNotice(report.message);
    },
  });

  const error = messageFor(loadError, "Could not load your sources.")
    ?? messageFor(startRun.error, "Could not start that Run.")
    // Not folded in below: `allSettled` means this fires only if the fan-out
    // itself broke, which is a different thing from some sources refusing.
    ?? messageFor(bulkDelete.error, "Could not delete those sources.");

  const summary = useMemo(() => {
    const list = visible;
    return {
      total: list.length,
      failing: list.filter((profile) => profile.last_run?.status === "failed").length,
      unreviewed: list.reduce((total, profile) => total + profile.unreviewed_binding_count, 0),
      players: list.reduce((total, profile) => total + (profile.last_run?.player_count ?? 0), 0),
    };
  }, [visible]);

  if (!profiles) {
    return (
      <Layout breadcrumbs={[{ label: "Sources" }]}>
        <Spinner label="Loading your sources" />
      </Layout>
    );
  }

  return (
    <Layout
      breadcrumbs={[{ label: "Sources" }]}
      title="Sources"
      description="Every place the portal pulls player statistics from, and how the last Run went."
      actions={
        visible.length > 0 ? (
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => navigate("/sources/new")}>
            Add source
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-lg">
        {error && <Banner tone="danger" title={error} />}

        {/* Its own Banner rather than another branch of `error`: what a bulk
            delete did is a receipt, and ranking it below a failed poll would
            let a four-second refresh erase the only record of it. */}
        {notice && <Banner tone="danger" title={notice} />}

        {visible.length === 0 ? (
          <EmptyState
            icon={<SourcesIcon size={20} />}
            title="No sources yet"
            action={
              <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => navigate("/sources/new")}>
                Add your first source
              </Button>
            }
          >
            A source is a URL that publishes player statistics. Paste one and the portal fetches it, shows
            you the fields it found, and lets you say which are the player's name, team, position and stats.
            It saves that as a Source Profile and reuses it on every Run.
          </EmptyState>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-md fold:grid-cols-4">
              <StatTile label="Sources" value={summary.total} hint="Configured Source Profiles" />
              <StatTile
                label="Needing attention"
                value={summary.failing}
                tone={summary.failing > 0 ? "danger" : "neutral"}
                hint={summary.failing > 0 ? "Last Run failed" : "Nothing failing"}
              />
              <StatTile
                label="Fields to review"
                value={summary.unreviewed}
                tone={summary.unreviewed > 0 ? "warning" : "neutral"}
                hint="Unclassified across all sources"
              />
              <StatTile
                label="Players"
                value={summary.players.toLocaleString()}
                hint="From each source's last Run"
              />
            </div>

            <Card
              title="All sources"
              description={
                narrowed
                  ? `Showing ${shown.length} of ${countOf(visible.length)}.`
                  : sort
                    ? "Every source in the workspace, in the order you asked for."
                    : "Every source in the workspace. Those whose last Run failed come first."
              }
              actions={
                // Only once the table is answering a narrower question than it
                // opened on. A permanently visible Clear is a control that does
                // nothing most of the time.
                narrowed || sort ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNarrowing(EVERYTHING);
                      setSort(null);
                    }}
                  >
                    Reset
                  </Button>
                ) : undefined
              }
              flush
            >
              {/*
                The filter bar, above the selection bar rather than below it:
                it is always there, and a bar that moved down the card whenever
                something was ticked would take the search box with it.
              */}
              <div className="flex flex-wrap items-center gap-sm border-b border-hairline px-lg py-sm">
                <TextInput
                  type="search"
                  className="h-control-sm w-auto min-w-[200px] flex-1 py-0 text-body-sm"
                  aria-label="Search sources"
                  placeholder="Search name, sport, league, season or who built it"
                  value={narrowing.query}
                  onChange={(event) =>
                    setNarrowing((before) => ({ ...before, query: event.target.value }))
                  }
                />
                <Select
                  className="select-sm w-auto"
                  aria-label="Filter by sport"
                  value={narrowing.sport}
                  // Through `chooseSport`, which drops a league the new sport
                  // does not have: a league belongs to one sport, so carrying
                  // one across would empty the table from a control nobody
                  // touched.
                  onChange={(event) =>
                    setNarrowing((before) => chooseSport(visible, before, event.target.value))
                  }
                >
                  <option value="">All sports</option>
                  {sports.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
                <Select
                  className="select-sm w-auto"
                  aria-label="Filter by league"
                  value={narrowing.league}
                  onChange={(event) =>
                    setNarrowing((before) => ({ ...before, league: event.target.value }))
                  }
                >
                  <option value="">
                    {narrowing.sport === "" ? "All leagues" : `All ${narrowing.sport} leagues`}
                  </option>
                  {leagues.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Only once something is ticked. A permanently visible bar with
                  a dead Delete button in it is a threat on every screen. */}
              {chosen.length > 0 && (
                <div className="border-b border-hairline bg-canvas px-lg py-sm">
                  {confirming ? (
                    <Banner
                      tone="danger"
                      title={`Delete ${countOf(chosen.length)}? This cannot be undone.`}
                    >
                      Their Bindings, Credential Sets and every Run they have recorded go with them.
                      {/* Named, not merely counted. The filter is the one thing
                          on this screen that can put a source in an
                          irreversible action while keeping it off the screen,
                          so the confirmation says which ones. */}
                      {hidden > 0 && (
                        <div className="mt-xs">
                          {hidden} of them {hidden === 1 ? "is" : "are"} hidden by the current
                          filter:{" "}
                          <span className="text-body-sm-medium">
                            {chosenProfiles
                              .filter((profile) => !shownIds.includes(profile.id))
                              .map((profile) => profile.name)
                              .join(", ")}
                          </span>
                          .
                        </div>
                      )}
                      <div className="mt-xs flex items-center gap-xs">
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={bulkDelete.isPending}
                          onClick={() => {
                            setNotice(null);
                            bulkDelete.mutate(chosenProfiles);
                          }}
                        >
                          {bulkDelete.isPending ? "Deleting…" : "Yes, delete them"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={bulkDelete.isPending}
                          onClick={() => setConfirming(false)}
                        >
                          Keep them
                        </Button>
                      </div>
                    </Banner>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-sm">
                      <span className="text-body-sm-medium text-ink">
                        {countOf(chosen.length)} selected
                        {hidden > 0 && (
                          <span className="font-normal text-muted"> · {hidden} hidden by the filter</span>
                        )}
                      </span>
                      <div className="flex items-center gap-xs">
                        <Button variant="ghost" size="sm" onClick={() => choose([])}>
                          Clear
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          icon={<TrashIcon size={14} />}
                          onClick={() => setConfirming(true)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {shown.length === 0 ? (
                /* Narrowed to nothing, which is a different thing from an
                   empty workspace and has to read as one — the sources are
                   still there, and the way back is a click rather than a
                   reload. The controls above stay on screen, so the term that
                   produced this is visible beside the answer. */
                <div className="px-lg py-xl text-center">
                  <p className="mb-xs text-body-sm-medium text-ink">No sources match.</p>
                  <p className="mb-md text-body-sm text-muted">
                    {countOf(visible.length)} in the workspace, none of them matching what the
                    controls above are asking for.
                  </p>
                  <Button size="sm" onClick={() => setNarrowing(EVERYTHING)}>
                    Clear the filters
                  </Button>
                </div>
              ) : (
              <TableScroll stickyHeader>
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-[1%]">
                        <Checkbox
                          aria-label={
                            headerState === "all"
                              ? "Clear the selection"
                              : narrowed
                                ? "Select every source shown"
                                : "Select every source"
                          }
                          checked={headerState === "all"}
                          indeterminate={headerState === "some"}
                          disabled={bulkDelete.isPending}
                          // Only the rows on screen, in either direction: with
                          // a filter on, the box at the top of the table speaks
                          // for the table rather than for the workspace.
                          onChange={() => choose(toggleShown(chosen, shownIds))}
                        />
                      </th>
                      <SortHeader column="name" label="Source" sort={sort} onSort={reorder} />
                      <SortHeader column="created_by" label="Created by" sort={sort} onSort={reorder} />
                      <SortHeader column="sport" label="Sport" sort={sort} onSort={reorder} />
                      <SortHeader column="league" label="League" sort={sort} onSort={reorder} />
                      <SortHeader column="season" label="Season" sort={sort} onSort={reorder} />
                      <SortHeader column="last_run" label="Last Run" sort={sort} onSort={reorder} />
                      <SortHeader column="last_success" label="Last success" sort={sort} onSort={reorder} />
                      <SortHeader column="players" label="Players" sort={sort} onSort={reorder} align="right" />
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((profile) => {
                      const inFlight =
                        profile.last_run?.status === "running" || profile.last_run?.status === "queued";
                      return (
                        <tr key={profile.id}>
                          <td>
                            <Checkbox
                              aria-label={`Select ${profile.name}`}
                              checked={chosen.includes(profile.id)}
                              disabled={bulkDelete.isPending}
                              onChange={() => choose(toggle(chosen, profile.id))}
                            />
                          </td>
                          <td>
                            <Link
                              to={`/sources/${profile.id}`}
                              className="text-body-sm-medium text-ink"
                            >
                              {profile.name}
                            </Link>
                            {profile.unreviewed_binding_count > 0 && (
                              <div className="mt-xxs">
                                <Link to={`/sources/${profile.id}/fields`} className="no-underline hover:no-underline">
                                  <Badge tone="warning">
                                    {profile.unreviewed_binding_count} fields to review
                                  </Badge>
                                </Link>
                              </div>
                            )}
                            {/* Only when it is on. A badge against every source
                                somebody runs by hand would bury the handful
                                that no longer need anybody. */}
                            {profile.schedule?.enabled && (
                              <div className="mt-xxs">
                                <Badge tone="accent">
                                  scheduled · {profile.schedule.cron} UTC
                                </Badge>
                              </div>
                            )}
                          </td>
                          {/*
                            Everyone sees every source (ADR-0016), so the list
                            has to say whose is whose — shared visibility
                            without attribution costs the ability to ask the
                            right person about a league.
                          */}
                          <td>
                            {profile.created_by ?? <Muted>—</Muted>}
                          </td>
                          {/*
                            Beside the League because the two are read together
                            and neither answers for the other: `fbs`, `fcs` and
                            `ncaa_d2` are all football, and `usl_super_league`
                            and `mls_next_pro` are both soccer. Printed exactly
                            as it is stored — the sport is the folder name in
                            the data file naming standard, and a prettified
                            spelling here would be a second name for a thing
                            that has one.
                          */}
                          <td>{profile.sport}</td>
                          {/*
                            Allowed to break mid-word, which nothing else in
                            this table is. A league is one unbroken token —
                            `ncaa_d1_women_soccer` is twenty characters with no
                            space in it — and an auto-layout table never
                            narrows a column below its longest word, so this one
                            was claiming 191px of a table with 1136px to live
                            in: a sixth of the width, to keep one identifier on
                            one line. Wrapping it is what buys the Sport column
                            its place without pushing the Scrape button off the
                            right edge.
                          */}
                          <td className="[overflow-wrap:anywhere]">{profile.league}</td>
                          <td>
                            <div className="flex items-center gap-xxs">
                              {profile.season}
                              {!profile.season_active && <Badge>closed</Badge>}
                            </div>
                          </td>
                          <td>
                            {profile.last_run ? (
                              <Link to={`/runs/${profile.last_run.id}`} className="no-underline hover:no-underline">
                                <StatusBadge status={profile.last_run.status} />
                              </Link>
                            ) : (
                              <StatusBadge status="never" />
                            )}
                            {profile.last_run?.status === "failed" && profile.last_run.failure_code && (
                              <div className="mt-xxs text-caption text-danger-ink">
                                {profile.last_run.failure_code.replace(/_/g, " ")}
                              </div>
                            )}
                          </td>
                          <td>
                            <Muted>{formatRelative(profile.last_successful_run_at)}</Muted>
                          </td>
                          <td className="text-right text-body-sm-medium text-ink">
                            {profile.last_run?.player_count?.toLocaleString() ?? <Muted>—</Muted>}
                          </td>
                          <td className="whitespace-nowrap text-right">
                            <Button
                              variant="primary"
                              size="sm"
                              icon={inFlight ? undefined : <PlayIcon size={14} />}
                              disabled={inFlight}
                              title={inFlight ? "This source already has a Run in progress." : undefined}
                              onClick={() => startRun.mutate(profile)}
                            >
                              {inFlight ? "Running…" : "Scrape"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableScroll>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

/**
 * A column heading that orders the table.
 *
 * A `<button>` inside the `<th>` rather than a click handler on the `<th>`
 * itself, which is the difference between a control and a cell that happens to
 * react to a mouse: this one is in the tab order, fires on Enter and on Space,
 * and is announced as a button. `aria-sort` goes on the `<th>`, where the
 * accessibility tree expects to find which way the column is running — and the
 * stylesheet reads the same attribute to mark the active heading, so what a
 * screen reader is told and what the eye is shown cannot drift.
 *
 * The `title` says what the *next* click does, including the third one. A
 * three-state control whose third state nobody finds is a two-state control.
 */
function SortHeader({
  column,
  label,
  sort,
  onSort,
  align = "left",
}: {
  column: SortColumn;
  label: string;
  sort: Sort | null;
  onSort: (column: SortColumn) => void;
  align?: "left" | "right";
}) {
  const active = sort?.column === column ? sort.direction : null;
  const next =
    active === null
      ? `Sort by ${label}`
      : nextSort(sort, column) === null
        ? `Back to the default order (failing sources first)`
        : `Sort by ${label}, the other way`;
  return (
    <th
      aria-sort={active === null ? "none" : active === "asc" ? "ascending" : "descending"}
      className={align === "right" ? "text-right" : undefined}
    >
      <button type="button" className="th-sort" title={next} onClick={() => onSort(column)}>
        {label}
        <SortIcon direction={active} />
      </button>
    </th>
  );
}

/** How many deletes are allowed in flight at once. */
const DELETE_CONCURRENCY = 6;

/** The human message an API rejection carries, or a fallback for anything else. */
function messageFor(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * Why one source in a fan-out delete did not go, or null if it did.
 *
 * A 404 counts as gone. The list polls, so a source deleted in another tab is
 * still drawn here until the next refetch; reporting "not found" for a row
 * somebody asked to delete would be an error message about a success, and
 * would keep the row ticked for a retry that can never work.
 */
function failureOf(result: PromiseSettledResult<void> | undefined): string | null {
  if (!result) return "Something went wrong.";
  if (result.status === "fulfilled") return null;
  if (result.reason instanceof ApiError) {
    return result.reason.status === 404 ? null : result.reason.message;
  }
  return "Something went wrong.";
}

/** "1 source", "4 sources". */
function countOf(count: number): string {
  return `${count} source${count === 1 ? "" : "s"}`;
}
